/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: Testimonials section - What Users Say About AI Image Generator
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Cut My Workload in Half',
    content:
      'I used to spend hours editing product photos for my shop. Most of that time went into fixing lighting and cleaning backgrounds. Now I can create new images in minutes and focus on packing orders instead. It has made my daily routine so much easier.',
    role: 'Online Store Owner',
    name: 'Emily Carter',
  },
  {
    title: 'Finally Matches What I Picture',
    content:
      'Explaining my ideas to designers was always tough, and the results often missed the mark. Now I can create sample images myself and show my team exactly what I want. It saves a lot of back-and-forth and keeps projects moving. I feel more confident sharing concepts now.',
    role: 'Marketing Lead',
    name: 'Jason Miller',
  },
  {
    title: 'Great for Daily Social Posts',
    content:
      'Posting every day used to feel stressful because I ran out of visuals so quickly. Now I can make new images in seconds, even when I am short on ideas. It keeps my feed active without long editing sessions. My engagement has grown since I started using it.',
    role: 'Content Creator',
    name: 'Chloe Adams',
  },
  {
    title: 'Perfect for Fast Client Drafts',
    content:
      'Clients often ask for quick mockups, and I never had enough time to design everything from scratch. Now I can show early concepts right away and explore more options before choosing a direction. It makes my workflow smoother and my clients happier. Turnaround time has improved a lot.',
    role: 'Freelance Designer',
    name: 'Lucas Reed',
  },
  {
    title: 'Helps My Students Understand Faster',
    content:
      'Some lessons are hard to explain with text alone, especially for visual learners. I now create simple images to show examples during class. It keeps students more engaged and helps them understand ideas more quickly. Preparing materials has also become much easier for me.',
    role: 'Teacher',
    name: 'Daniel Brook',
  },
  {
    title: 'Fun for My Creative Projects',
    content:
      'I love making small art projects, but I am not great at drawing. Now I can turn my ideas into images without needing advanced skills. I try different styles until something feels right. It has made my hobby a lot more fun.',
    role: 'Hobby Artist',
    name: 'Sarah Collins',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
